import { useState } from "react";

const BODY_REGIONS = [
  { id: "head",            label: "Head",          x: 85,  y: 20,  r: 18 },
  { id: "throat",          label: "Throat",        x: 85,  y: 48,  r: 10 },
  { id: "chest",           label: "Chest",         x: 85,  y: 75,  r: 18 },
  { id: "solar_plexus",    label: "Solar Plexus",  x: 85,  y: 105, r: 14 },
  { id: "abdomen",         label: "Abdomen",       x: 85,  y: 130, r: 14 },
  { id: "pelvis",          label: "Pelvis",        x: 85,  y: 158, r: 14 },
  { id: "left_shoulder",   label: "L. Shoulder",   x: 55,  y: 65,  r: 10 },
  { id: "right_shoulder",  label: "R. Shoulder",   x: 115, y: 65,  r: 10 },
  { id: "left_arm",        label: "L. Arm",        x: 40,  y: 100, r: 10 },
  { id: "right_arm",       label: "R. Arm",        x: 130, y: 100, r: 10 },
  { id: "left_hand",       label: "L. Hand",       x: 30,  y: 135, r: 8  },
  { id: "right_hand",      label: "R. Hand",       x: 140, y: 135, r: 8  },
  { id: "left_thigh",      label: "L. Thigh",      x: 70,  y: 190, r: 12 },
  { id: "right_thigh",     label: "R. Thigh",      x: 100, y: 190, r: 12 },
  { id: "left_knee",       label: "L. Knee",       x: 68,  y: 220, r: 10 },
  { id: "right_knee",      label: "R. Knee",       x: 102, y: 220, r: 10 },
  { id: "left_foot",       label: "L. Foot",       x: 65,  y: 265, r: 10 },
  { id: "right_foot",      label: "R. Foot",       x: 105, y: 265, r: 10 },
  { id: "back_head",       label: "Back of Head",  x: 85,  y: 20,  r: 18, back: true },
  { id: "neck",            label: "Neck",          x: 85,  y: 48,  r: 10, back: true },
  { id: "upper_back",      label: "Upper Back",    x: 85,  y: 75,  r: 18, back: true },
  { id: "mid_back",        label: "Mid Back",      x: 85,  y: 105, r: 14, back: true },
  { id: "lower_back",      label: "Lower Back",    x: 85,  y: 130, r: 14, back: true },
  { id: "glutes",          label: "Glutes",        x: 85,  y: 158, r: 14, back: true },
  { id: "left_hamstring",  label: "L. Hamstring",  x: 70,  y: 190, r: 12, back: true },
  { id: "right_hamstring", label: "R. Hamstring",  x: 100, y: 190, r: 12, back: true },
  { id: "left_calf",       label: "L. Calf",       x: 68,  y: 230, r: 10, back: true },
  { id: "right_calf",      label: "R. Calf",       x: 102, y: 230, r: 10, back: true },
];

// Sensation → color mapping
const SENSATION_COLORS = {
  // Warmth/energy — amber
  "Warmth":     "#f59e0b",
  "Radiant":    "#fbbf24",
  "Rushing":    "#f97316",
  // Tingling/electric — cyan
  "Tingling":   "#22d3ee",
  "Buzzing":    "#06b6d4",
  "Vibration":  "#67e8f9",
  "Electric":   "#a5f3fc",
  // Tension/pressure — red
  "Tension":    "#ef4444",
  "Pressure":   "#dc2626",
  "Tightness":  "#f87171",
  "Contracting":"#b91c1c",
  // Openness/expansion — green
  "Openness":   "#22c55e",
  "Expansive":  "#4ade80",
  "Relaxation": "#86efac",
  "Flowing":    "#6ee7b7",
  // Grounding/stillness — teal
  "Grounded":   "#14b8a6",
  "Rooted":     "#0d9488",
  "Still":      "#99f6e4",
  // Dissolution/merging — purple
  "Dissolving": "#a855f7",
  "Dissolution":"#9333ea",
  "Merging":    "#c084fc",
  "Waves":      "#d8b4fe",
  // Heaviness/numbness — slate
  "Heaviness":  "#64748b",
  "Numbness":   "#94a3b8",
  "Pulsing":    "#7dd3fc",
  // Vulnerability — rose
  "Raw":        "#fb7185",
  "Tender":     "#fda4af",
  "Lightness":  "#f0abfc",
};

const DEFAULT_COLOR = "#94a3b8";

function getSensationColor(sensation) {
  return SENSATION_COLORS[sensation] || DEFAULT_COLOR;
}

// Parse raw body_sensations rows into region frequency + dominant sensation map
function processHeatmapData(rows) {
  const regionMap = {}; // { regionId: { count, sensationCounts: { sensation: n } } }

  rows.forEach(row => {
    let sensations = row.body_sensations;
    if (typeof sensations === "string") {
      try { sensations = JSON.parse(sensations); } catch { return; }
    }
    if (!Array.isArray(sensations)) return;

    sensations.forEach(item => {
      const regionId = item?.region;
      const sensation = item?.sensation || "";
      if (!regionId) return;

      if (!regionMap[regionId]) regionMap[regionId] = { count: 0, sensationCounts: {} };
      regionMap[regionId].count++;
      if (sensation) {
        regionMap[regionId].sensationCounts[sensation] =
          (regionMap[regionId].sensationCounts[sensation] || 0) + 1;
      }
    });
  });

  // Find max count for opacity normalization
  const maxCount = Math.max(...Object.values(regionMap).map(r => r.count), 1);

  // Build final map with dominant sensation and opacity
  const result = {};
  Object.entries(regionMap).forEach(([regionId, data]) => {
    const dominantSensation = Object.entries(data.sensationCounts)
      .sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    result[regionId] = {
      count: data.count,
      opacity: 0.2 + (data.count / maxCount) * 0.75,
      dominantSensation,
      color: getSensationColor(dominantSensation),
      sensationCounts: data.sensationCounts,
    };
  });

  return result;
}

function generateSummary(heatmapData, medicineLabel) {
  if (Object.keys(heatmapData).length === 0) return null;

  const sorted = Object.entries(heatmapData)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 4);

  const topRegions = sorted.map(([regionId, data]) => {
    const region = BODY_REGIONS.find(r => r.id === regionId);
    return {
      label: region?.label || regionId,
      sensation: data.dominantSensation,
      count: data.count,
    };
  });

  const topTwo = topRegions.slice(0, 2);
  const sensationList = topRegions
    .filter(r => r.sensation)
    .map(r => `${r.sensation.toLowerCase()} in the ${r.label.toLowerCase()}`)
    .slice(0, 3)
    .join(", ");

  let summary = `During your ${medicineLabel} sessions, `;
  summary += `the ${topTwo.map(r => r.label.toLowerCase()).join(" and ")} are your most frequently activated regions. `;
  if (sensationList) {
    summary += `Common experiences include ${sensationList}. `;
  }

  // Highlight dominant sensation categories
  const allSensations = Object.values(heatmapData)
    .map(d => d.dominantSensation)
    .filter(Boolean);
  const hasDissolving = allSensations.some(s => ["Dissolving", "Dissolution", "Merging", "Waves"].includes(s));
  const hasGrounding = allSensations.some(s => ["Grounded", "Rooted", "Still"].includes(s));
  const hasTension = allSensations.some(s => ["Tension", "Tightness", "Contracting", "Pressure"].includes(s));

  if (hasDissolving) summary += "Dissolution and boundary-softening sensations appear regularly in your body experience. ";
  if (hasGrounding) summary += "Your body tends toward grounding and stillness. ";
  if (hasTension) summary += "Some areas of held tension are recurring, worth bringing awareness to these zones. ";

  return summary.trim();
}

function BodyHeatmap({ rows, medicineLabel, isMushroom }) {
  const [view, setView] = useState("front");
  const [hoveredRegion, setHoveredRegion] = useState(null);

  const heatmapData = processHeatmapData(rows);
  const summary = generateSummary(heatmapData, medicineLabel);
  const visibleRegions = BODY_REGIONS.filter(r => view === "back" ? r.back : !r.back);

  const hoveredData = hoveredRegion ? heatmapData[hoveredRegion] : null;
  const hoveredRegionLabel = BODY_REGIONS.find(r => r.id === hoveredRegion)?.label;

  return (
    <div>
      {/* Front/Back toggle */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        {["front", "back"].map(v => (
          <span key={v} onClick={() => setView(v)} style={{
            cursor: "pointer", padding: "4px 14px", borderRadius: "20px", fontSize: "12px",
            border: view === v ? "1px solid rgba(125,211,232,0.8)" : "1px solid rgba(255,255,255,0.2)",
            background: view === v ? "rgba(125,211,232,0.15)" : "rgba(255,255,255,0.05)",
            color: view === v ? "#7dd3e8" : "inherit",
          }}>
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </span>
        ))}
      </div>

      <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* SVG body */}
        <svg width="170" height="290" style={{ flexShrink: 0 }}>
          <ellipse cx="85" cy="20" rx="18" ry="18" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />
          <rect x="62" y="55" width="46" height="95" rx="8" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />
          <rect x="32" y="58" width="30" height="80" rx="8" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />
          <rect x="108" y="58" width="30" height="80" rx="8" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />
          <rect x="63" y="148" width="22" height="90" rx="8" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />
          <rect x="87" y="148" width="22" height="90" rx="8" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />

          {visibleRegions.map(region => {
            const data = heatmapData[region.id];
            const isHovered = hoveredRegion === region.id;
            return (
              <circle
                key={region.id}
                cx={region.x} cy={region.y} r={region.r}
                fill={data
                  ? `${data.color}${Math.round(data.opacity * 255).toString(16).padStart(2, "0")}`
                  : "rgba(255,255,255,0.03)"}
                stroke={isHovered
                  ? "rgba(255,255,255,0.9)"
                  : data ? data.color : "rgba(255,255,255,0.1)"}
                strokeWidth={isHovered ? 2 : 1}
                style={{ cursor: data ? "pointer" : "default", transition: "all 0.15s" }}
                onMouseEnter={() => data && setHoveredRegion(region.id)}
                onMouseLeave={() => setHoveredRegion(null)}
              />
            );
          })}
        </svg>

        {/* Right panel */}
        <div style={{ flex: 1, minWidth: "160px" }}>
          {/* Hover tooltip */}
          {hoveredData && (
            <div style={{
              background: "rgba(255,255,255,0.07)", borderRadius: "10px",
              padding: "12px 14px", marginBottom: "16px",
              border: `1px solid ${hoveredData.color}44`,
            }}>
              <div style={{ fontSize: "13px", fontWeight: "600", marginBottom: "6px", color: hoveredData.color }}>
                {hoveredRegionLabel}
              </div>
              <div style={{ fontSize: "12px", opacity: 0.7, marginBottom: "6px" }}>
                {hoveredData.count} {hoveredData.count === 1 ? "session" : "sessions"}
              </div>
              {Object.entries(hoveredData.sensationCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([s, n]) => (
                  <div key={s} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: getSensationColor(s), flexShrink: 0 }} />
                    <span style={{ fontSize: "12px" }}>{s}</span>
                    <span style={{ fontSize: "11px", opacity: 0.5, marginLeft: "auto" }}>{n}×</span>
                  </div>
                ))}
            </div>
          )}

          {/* Top activated regions */}
          {Object.keys(heatmapData).length > 0 && (
            <div>
              <div style={{ fontSize: "11px", opacity: 0.5, marginBottom: "8px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                Most Activated
              </div>
              {Object.entries(heatmapData)
                .sort((a, b) => b[1].count - a[1].count)
                .slice(0, 5)
                .map(([regionId, data]) => {
                  const region = BODY_REGIONS.find(r => r.id === regionId);
                  return (
                    <div key={regionId} style={{
                      display: "flex", alignItems: "center", gap: "8px",
                      padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.05)",
                    }}>
                      <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: data.color, flexShrink: 0 }} />
                      <span style={{ fontSize: "12px", flex: 1 }}>{region?.label}</span>
                      {data.dominantSensation && (
                        <span style={{ fontSize: "11px", opacity: 0.6 }}>{data.dominantSensation}</span>
                      )}
                      <span style={{ fontSize: "11px", opacity: 0.4 }}>{data.count}×</span>
                    </div>
                  );
                })}
            </div>
          )}

          {/* Legend */}
          <div style={{ marginTop: "16px" }}>
            <div style={{ fontSize: "11px", opacity: 0.5, marginBottom: "8px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
              Sensation Colors
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {[
                { label: "Warmth / Energy", color: "#f59e0b" },
                { label: "Tingling / Electric", color: "#22d3ee" },
                { label: "Tension / Pressure", color: "#ef4444" },
                { label: "Openness / Flow", color: "#22c55e" },
                { label: "Grounding", color: "#14b8a6" },
                { label: "Dissolution", color: "#a855f7" },
                { label: "Heaviness / Pulse", color: "#64748b" },
                { label: "Vulnerability", color: "#fb7185" },
              ].map(({ label, color }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: color, flexShrink: 0 }} />
                  <span style={{ fontSize: "10px", opacity: 0.6 }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div style={{
          background: "rgba(125,211,232,0.07)", border: "1px solid rgba(125,211,232,0.2)",
          borderRadius: "10px", padding: "16px 20px", marginTop: "16px",
          fontSize: "14px", lineHeight: "1.7", opacity: 0.9,
        }}>
          🗺️ {summary}
        </div>
      )}

      {Object.keys(heatmapData).length === 0 && (
        <p style={{ fontSize: "13px", opacity: 0.5, fontStyle: "italic" }}>
          No body map data yet for {medicineLabel} sessions.
        </p>
      )}
    </div>
  );
}

function SomaticHeatmapInsight({ rows, medicineType }) {
  const isMushroom = medicineType === "mushroom";
  const isAll = medicineType === "all";

  if (isAll) {
    const cannabisRows = rows.filter(r => r.medicine_type === "cannabis");
    const mushroomRows = rows.filter(r => r.medicine_type === "mushroom");
    const hasCannabis = cannabisRows.length > 0;
    const hasMushroom = mushroomRows.length > 0;
    if (!hasCannabis && !hasMushroom) return null;

    return (
      <div>
        {hasCannabis && hasMushroom ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px" }}>
            <div>
              <div style={{ fontSize: "13px", opacity: 0.6, marginBottom: "12px", fontWeight: "500" }}>🌿 Cannabis</div>
              <BodyHeatmap rows={cannabisRows} medicineLabel="cannabis" isMushroom={false} />
            </div>
            <div>
              <div style={{ fontSize: "13px", opacity: 0.6, marginBottom: "12px", fontWeight: "500" }}>🌀 Psychedelics</div>
              <BodyHeatmap rows={mushroomRows} medicineLabel="mushroom" isMushroom={true} />
            </div>
          </div>
        ) : hasCannabis ? (
          <BodyHeatmap rows={cannabisRows} medicineLabel="cannabis" isMushroom={false} />
        ) : (
          <BodyHeatmap rows={mushroomRows} medicineLabel="mushroom" isMushroom={true} />
        )}
      </div>
    );
  }

  return (
    <BodyHeatmap
      rows={rows}
      medicineLabel={isMushroom ? "mushroom" : "cannabis"}
      isMushroom={isMushroom}
    />
  );
}

export default SomaticHeatmapInsight;
